import { Accordion, AccordionDetails, Grid, InputAdornment, Typography } from '@mui/material';
import { Email, ExpandMore } from '@mui/icons-material';
import { useEffect, useState } from 'react';
import { PmiButton } from 'shell/components/shared/button';
import { FormikProps } from 'formik';
import { AccordionSummary } from 'shell/components/shared/summary-accordion';
import PmiTextField from 'shell/components/shared/text-field';
import { DatePicker } from '@mui/lab';
import StateElaborationPecList from './state-elaboration-pec-list';
import { FiltersFormPec } from 'shell/types/types-pec';
import * as yup from 'yup';
import OperatorList from 'civil-status/components/shared/operator-list';
import { useUserProfile } from 'shell/hooks/use-user-profile';
import { useEventCallback } from '@mui/material';
import { SelectChangeEvent } from '@mui/material';
import ListTypeComunication from '../../list-type-comunication';

export const validationSchemaFilterPec = yup.object({
  emailSender: yup.string().email('Campo email non valido'),
  emailReceiver: yup.string().email('Campo email non valido'),
  startDate: yup.date().nullable().typeError('Data non valida'),
  endDate: yup
    .date()
    .nullable()
    .typeError('Data non valida')
    .when('startDate', (startDate, schema) =>
      startDate
        ? schema.min(startDate, 'La data di fine deve essere successiva alla data di inizio')
        : schema
    ),
});

interface FiltersPecProps {
  filters: FormikProps<FiltersFormPec>;
}

const FiltersPec = ({ filters }: FiltersPecProps): React.ReactElement => {
  const [open, setOpen] = useState<boolean>(true);
  const [currentCodeOperator, setCurrentCodeOperator] = useState<string | undefined>(undefined);
  const { userProfile } = useUserProfile();

  const onChangeOperator = useEventCallback((event: SelectChangeEvent<string>) => {
    filters.setFieldValue('codOperator', event.target.value);
  });

  useEffect(() => {
    if (userProfile.datiLogin?.key?.userName) {
      setCurrentCodeOperator(userProfile.datiLogin?.key?.userName);
    }
  }, [userProfile.datiLogin?.key?.userName]);

  const setIsInitialOperator = useEventCallback((isInitialOperator) => {
    // se presente nella lista inizializzare il campo
    if (isInitialOperator) {
      filters.setFieldValue('codOperator', currentCodeOperator);
    }
  });

  return (
    <Accordion
      expanded={open}
      onChange={() => {
        setOpen(!open);
      }}
    >
      <AccordionSummary
        aria-controls="panel-filters-pec-content"
        id="panel-filters-pec-header"
        classes={{
          root: 'pmi-flex-row-reverse',
        }}
        expandIcon={<ExpandMore />}
      >
        <Typography>Filtri</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <form onSubmit={filters.handleSubmit} onReset={filters.handleReset}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <DatePicker
                label="Inserita da"
                aria-label="Da"
                value={filters.values.startDate || null}
                onChange={(date) => filters.setFieldValue('startDate', date)}
                renderInput={(params) => (
                  <PmiTextField
                    {...params}
                    id="startDate"
                    error={filters.touched.startDate && Boolean(filters.errors.startDate)}
                    helperText={filters.errors.startDate}
                    fullWidth
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <DatePicker
                label="Inserita a"
                aria-label="Inserita a"
                value={filters.values.endDate || null}
                onChange={(date) => filters.setFieldValue('endDate', date)}
                renderInput={(params) => (
                  <PmiTextField
                    {...params}
                    id="endDate"
                    error={filters.touched.endDate && Boolean(filters.errors.endDate)}
                    helperText={filters.errors.endDate}
                    fullWidth
                  />
                )}
              />
            </Grid>
            {/* <Grid item xs={12} md={3}>
              <PmiTextField
                aria-label="Mittente"
                label="Mittente"
                id="emailSender"
                name="emailSender"
                fullWidth
                type="text"
                variant="outlined"
                value={filters.values.emailSender}
                onChange={filters.handleChange}
                error={filters.touched.emailSender && Boolean(filters.errors.emailSender)}
                helperText={filters.errors.emailSender}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Email />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid> */}
            <Grid item xs={12} md={3}>
              <PmiTextField
                aria-label="Descrizione destinatario"
                label="Descrizione destinatario"
                id="descriptionReceiver"
                name="descriptionReceiver"
                fullWidth
                type="text"
                variant="outlined"
                value={filters.values.descriptionReceiver}
                onChange={filters.handleChange}
                error={
                  filters.touched.descriptionReceiver && Boolean(filters.errors.descriptionReceiver)
                }
                helperText={filters.errors.descriptionReceiver}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <PmiTextField
                aria-label="PEC destinatario"
                label="PEC destinatario"
                id="emailReceiver"
                name="emailReceiver"
                fullWidth
                type="text"
                variant="outlined"
                value={filters.values.emailReceiver}
                onChange={filters.handleChange}
                error={filters.touched.emailReceiver && Boolean(filters.errors.emailReceiver)}
                helperText={filters.errors.emailReceiver}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Email />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <PmiTextField
                aria-label="Messaggio"
                label="Messaggio"
                id="message"
                name="message"
                fullWidth
                type="text"
                variant="outlined"
                value={filters.values.message}
                onChange={filters.handleChange}
                error={filters.touched.message && Boolean(filters.errors.message)}
                helperText={filters.errors.message}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <StateElaborationPecList
                value={filters.values.state}
                onChange={(el) => filters.setFieldValue('state', el)}
                error={filters.touched.state && Boolean(filters.errors.state)}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <ListTypeComunication
                value={filters.values.typeComunication}
                onChange={(el) => filters.setFieldValue('typeComunication', el)}
                error={filters.touched.typeComunication && Boolean(filters.errors.typeComunication)}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <OperatorList
                onChange={onChangeOperator}
                value={filters.values.codOperator || ''}
                // codiceStatoEvento={formContext.values.stateAct || undefined}
                // disabled={!formContext.values.stateAct}
                currentCodeOperator={currentCodeOperator}
                setIsInitialOperator={setIsInitialOperator}
              />
            </Grid>

            <Grid item container spacing={2} xs={12} justifyContent="space-between" display="flex">
              <Grid item container spacing={2} xs={12} justifyContent="flex-end" display="flex">
                <Grid item xs={12} md={2}>
                  <PmiButton variant="contained" color="secondary" fullWidth type="reset">
                    Pulisci
                  </PmiButton>
                </Grid>
                <Grid item xs={12} md={2}>
                  <PmiButton
                    variant="contained"
                    color="secondary"
                    fullWidth
                    type="submit"
                    disabled={!filters.isValid}
                  >
                    Cerca
                  </PmiButton>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </form>
      </AccordionDetails>
    </Accordion>
  );
};

FiltersPec.displayName = 'Filters Certificate Anpr component';

export default FiltersPec;
